import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Calendar, CalendarDayButton } from '@/components/ui/calendar'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { dateKey } from './format'

interface IProps {
  month: Date
  onMonthChange: (month: Date) => void
  selected?: Date
  onSelect: (date: Date) => void
  // Keys for the visible month plus the trailing next-month days shown in the grid
  slots: Record<string, string[]>
  loading: boolean
}

export default function MonthCalendar({ month, onMonthChange, selected, onSelect, slots, loading }: IProps) {
  const { t, i18n } = useTranslation()
  // Availability alone decides which days are enabled (the server filters past days and booking notice).
  const isAvailable = (date: Date) => !!slots[dateKey(date)]?.length
  const monthStart = new Date(month.getFullYear(), month.getMonth(), 1)
  const currentMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  const shiftMonth = (delta: number) => onMonthChange(new Date(month.getFullYear(), month.getMonth() + delta, 1))

  const monthName = new Intl.DateTimeFormat(i18n.language, { month: 'long' }).format(month)
  const weekdayFormat = new Intl.DateTimeFormat(i18n.language, { weekday: 'short' })
  const shortMonthFormat = new Intl.DateTimeFormat(i18n.language, { month: 'short' })

  return (
    <div className='flex w-full flex-col'>
      <div className='mb-3 flex items-center justify-between'>
        <span className='text-base font-semibold text-foreground'>
          {monthName} <span className='font-medium text-muted-foreground'>{month.getFullYear()}</span>
        </span>
        <div className='flex items-center'>
          <Button variant='ghost' size='icon-sm' aria-label={t('Scheduling::Previous month')} disabled={monthStart <= currentMonth} onClick={() => shiftMonth(-1)}>
            <ChevronLeft />
          </Button>
          <Button variant='ghost' size='icon-sm' aria-label={t('Scheduling::Next month')} onClick={() => shiftMonth(1)}>
            <ChevronRight />
          </Button>
        </div>
      </div>
      <div className='relative'>
        <Calendar
          mode='single'
          month={month}
          onMonthChange={onMonthChange}
          selected={selected}
          onSelect={(date) => date && onSelect(date)}
          disabled={(date) => !isAvailable(date)}
          // Trailing next-month days stay visible (and bookable) like cal.com; leading ones are blanked
          hidden={{ before: monthStart }}
          fixedWeeks
          hideNavigation
          className='w-full! p-0 [--cell-radius:var(--radius-md)] [--cell-size:0px]'
          formatters={{ formatWeekdayName: (date) => weekdayFormat.format(date) }}
          classNames={{
            month_caption: 'hidden',
            weekdays: 'flex gap-1',
            weekday: 'flex-1 pb-3 text-center text-xs font-medium tracking-widest text-foreground uppercase select-none',
            week: 'mt-1 flex w-full gap-1',
            day: 'group/day relative aspect-square w-full flex-1 p-0 text-center select-none',
            today: '[&>button]:after:absolute [&>button]:after:bottom-[18%] [&>button]:after:left-1/2 [&>button]:after:size-1 [&>button]:after:-translate-x-1/2 [&>button]:after:rounded-full [&>button]:after:bg-current',
            outside: '',
            disabled: '[&>button]:font-light [&>button]:text-foreground/80 [&>button]:opacity-100!',
            hidden: 'invisible',
          }}
          modifiers={{ available: isAvailable }}
          modifiersClassNames={{
            available: '[&>button]:bg-foreground/10 [&>button]:font-medium [&>button]:hover:bg-foreground/10 [&>button]:hover:ring-2 [&>button]:hover:ring-foreground [&>button]:data-[selected-single=true]:bg-primary',
          }}
          components={{
            DayButton: ({ day, modifiers, children, ...props }) => (
              <CalendarDayButton day={day} modifiers={modifiers} {...props} className='size-full rounded-(--cell-radius) text-sm'>
                {day.outside && day.date.getDate() === 1 && (
                  <span className='absolute top-1 left-1 rounded-sm px-1 text-[10px]! font-semibold tracking-wide uppercase opacity-100!'>
                    {shortMonthFormat.format(day.date).replace('.', '')}
                  </span>
                )}
                {children as React.ReactNode}
              </CalendarDayButton>
            ),
          }}
        />
        {loading && <Skeleton className='absolute inset-0 opacity-40' />}
      </div>
    </div>
  )
}
